"use client";

import { motion, useReducedMotion } from "framer-motion";
import { MapPin, Building2, Award, ShieldAlert, ArrowRight, CircleCheck as CheckCircle2, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
export default function LocalAuthority() {
  const shouldReduceMotion = useReducedMotion();
  const corePillars = [{
    icon: MapPin,
    title: "NAP consistency across the web",
    description: "I audit and standardise your business name, address, and phone number across core mapping ecosystems, UK data aggregators, and primary consumer platforms. Uniform data eliminates ambiguity and strengthens search engine confidence in your exact location.",
    highlights: ["Direct audit of core mapping platforms", "Deduplication of conflicting records", "Structured schema alignment"]
  }, {
    icon: Building2,
    title: "Curated local & industry directories",
    description: "I hand-select and manually optimise listings in reputable, industry-specific UK trade hubs, accredited bodies, and established regional directories. Every profile is properly categorised, claimed, and maintained with complete business details.",
    highlights: ["Vetted UK business directories only", "Manual profile completion & verification", "Accreditation & trade association links"]
  }, {
    icon: Award,
    title: "Legitimate local authority & earned mentions",
    description: "true local ranking resilience comes from authentic digital PR, regional community initiatives, and genuine business collaborations. I help you secure natural, un-manipulated mentions that search engines value and reward over time.",
    highlights: ["Authentic regional digital PR", "Community partnership coverage", "Earned editorial citations"]
  }];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="localcitationsauthority" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      {/* Decorative subtle background accents */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 right-0 h-96 w-96 rounded-full bg-secondary/30 blur-3xl" />
        <div className="absolute bottom-10 left-0 h-80 w-80 rounded-full bg-muted/40 blur-3xl" />
      </div>

      <div className="container relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        {/* Header Block: 3-col kicker & 9-col main heading & intro */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:gap-12 pb-14 border-b border-border">
          <div className="lg:col-span-4">
            <Badge variant="secondary" className="px-3.5 py-1 text-xs font-medium uppercase tracking-wider text-primary border border-border">
              <Sparkles className="size-3.5 mr-1 text-accent" />
              Local presence & trust
            </Badge>
            <p className="mt-4 text-sm font-medium text-muted-foreground uppercase tracking-wide">
              Direct consultant guidance
            </p>
          </div>

          <div className="lg:col-span-8 space-y-4">
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
              Local citations and local authority
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-3xl">
              I build clear, enduring search signals through precise NAP consistency, vetted UK directories, and genuine local references. Working directly with me ensures your business establishes legitimate prominence without relying on artificial shortcuts.
            </p>
          </div>
        </div>

        {/* Core Pillars Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3 lg:gap-8">
          {corePillars.map((pillar, pillarIndex) => {
          const IconComponent = pillar.icon;
          return <motion.div key={pillar.title} data-index={pillarIndex} variants={itemVariants} whileHover={shouldReduceMotion ? {} : {
            y: -4
          }} className="h-full">
                <Card className="h-full flex flex-col justify-between border-border bg-card/70 backdrop-blur-xs shadow-sm transition-[border-color,box-shadow] duration-300 hover:border-primary/40 hover:shadow-md">
                  <div>
                    <CardHeader className="space-y-4 pb-4">
                      <div className="flex size-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <IconComponent className="size-6" />
                      </div>
                      <CardTitle className="font-serif text-xl font-bold text-foreground">
                        {pillar.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                        {pillar.description}
                      </p>
                      <ul className="space-y-2.5 pt-2 border-t border-border/60">
                        {pillar.highlights.map((highlight, highlightIndex) => <li key={highlightIndex} data-index={highlightIndex} className="flex items-start text-xs sm:text-sm text-foreground/90">
                            <CheckCircle2 className="size-4 text-accent shrink-0 mr-2 mt-0.5" />
                            <span>{highlight}</span>
                          </li>)}
                      </ul>
                    </CardContent>
                  </div>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Ethical Standpoint Callout Panel */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1]
      }} className="mt-14 rounded-2xl border border-destructive/20 bg-muted/30 p-6 sm:p-8 lg:p-10 shadow-xs">
          <div className="flex flex-col lg:flex-row items-start lg:items-center gap-6 justify-between">
            <div className="flex items-start gap-4 sm:gap-5 max-w-3xl">
              <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-destructive/10 text-destructive mt-1">
                <ShieldAlert className="size-6" />
              </div>
              <div className="space-y-2">
                <div className="inline-flex items-center gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wider text-destructive">
                    My ethical standard
                  </span>
                  <span className="h-1 w-1 rounded-full bg-destructive/40" />
                  <span className="text-xs text-muted-foreground font-medium">
                    Protection for your brand
                  </span>
                </div>
                <h3 className="font-serif text-xl sm:text-2xl font-bold text-foreground">
                  Zero spam directories. No automated citation blasts.
                </h3>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  I explicitly do not recommend or use low-quality link schemes, spun directory blast tools, or paid link farms. These outdated tactics risk manual search penalties and compromise your brand reputation. Every local reference I build for you is verified, relevant, and transparent.
                </p>
              </div>
            </div>

            <div className="shrink-0 w-full lg:w-auto">
              <div className="rounded-xl border border-border bg-card p-4 sm:p-5 text-center lg:text-left shadow-2xs">
                <p className="text-xs font-medium text-muted-foreground">Direct consultant access</p>
                <p className="text-sm font-semibold text-foreground mt-0.5">100% manual, bespoke execution</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* CTA Footer Block */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.1,
        ease: [0.16, 1, 0.3, 1]
      }} className="mt-14 rounded-2xl bg-primary text-primary-foreground p-8 sm:p-12 text-center shadow-lg relative overflow-hidden">
          {/* Subtle background glow inside CTA */}
          <div className="pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full bg-accent/20 blur-2xl" />
          <div className="pointer-events-none absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-background/10 blur-2xl" />

          <div className="relative mx-auto max-w-2xl space-y-4">
            <h3 className="font-serif text-2xl sm:text-3xl font-bold text-primary-foreground tracking-tight">
              Ready to strengthen your local search authority?
            </h3>
            <p className="text-sm sm:text-base text-primary-foreground/80 leading-relaxed">
              Book a direct consultation with me to review your current local footprint, clean up conflicting citations, and map out a clean authority strategy.
            </p>
            <div className="pt-4 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button asChild size="lg" className="w-full sm:w-auto bg-background text-foreground hover:bg-background/90 shadow-md font-medium text-base h-12 px-8">
                <Link to="/ContactPage">
                  Book a Free SEO Consultation
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}